import { graphql } from "gatsby"
import { MapDataToPropsCtx } from "../components/MapSlicesToComponents"

import type { PageDataBodyHtmlFragment } from "../graphql.gen"
import { SliceContainer } from "../components/SliceContainer"
import React from "react"

type Props = ReturnType<typeof mapDataToProps>

export const PageDataBodyHtml = ({ embedCode }: Props) => {
	const rDiv = React.useRef<HTMLDivElement>(null)

	// Embedded HTML with <script> tags will not run if they are injected
	// `innerHTML` or `__dangerouslySetInnerHTML`. We'll need to manually
	// extract and re-inject them to ensure that they execute.
	//
	// We also need to fire events that would normally be fired on hard-reload
	// like "readystatechange" since during client-side navigation those events
	// don't normally fire.
	React.useEffect(() => {
		const div = rDiv.current
		if (!embedCode || !div) return

		div.innerHTML = embedCode

		// Get all scripts in document order
		const scripts = Array.from(div.querySelectorAll("script"))

		const executeScriptsInOrder = async () => {
			for (const s of scripts) {
				await new Promise<void>((resolve) => {
					const script = document.createElement("script")

					const attrsToCopy = Array.from(s.attributes)
					attrsToCopy.forEach((a) => script.setAttribute(a.name, a.value))
					script.textContent = s.textContent

					if (script.src) {
						script.onload = () => {
							document.dispatchEvent(new Event("readystatechange"))
							resolve()
						}

						script.onerror = () => resolve()
					}

					div.removeChild(s)
					div.appendChild(script)

					if (!script.src) {
						resolve()
					}
				})
			}
		}

		executeScriptsInOrder()
	}, [embedCode])

	if (!embedCode) return null

	return (
		<SliceContainer backgroundColor="white">
			<div ref={rDiv} />
		</SliceContainer>
	)
}

export function mapDataToProps({
	data,
}: MapDataToPropsCtx<PageDataBodyHtmlFragment>) {
	return {
		embedCode: data.primary.embed_code,
	}
}

export const fragment = graphql`
	fragment PageDataBodyHtml on PrismicPageDataBodyHtml {
		primary {
			embed_code
		}
	}
`

export default PageDataBodyHtml
